import React, { useState, useEffect } from "react";
import api from "./api";
import Fuse from "fuse.js";

const initialState = {
  name: "",
  catalog_number: "",
  model: "",
  location: "",
  quantity: 0,
  unit: "",
  price: "",
  price_type: "constant",
  description: "",
  auction_platform: [],
  auction_allegro_url: "",
  auction_olx_url: "",
  images: [],
  is_flagged: false,
};

const ProductForm = () => {
  const [form, setForm] = useState(initialState);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [allProductNames, setAllProductNames] = useState([]);
  const [suggestedName, setSuggestedName] = useState("");
  const [allModels, setAllModels] = useState([]);
  const [suggestedModel, setSuggestedModel] = useState("");
  const [allLocations, setAllLocations] = useState([]);
  const [suggestedLocation, setSuggestedLocation] = useState("");

  useEffect(() => {
    api.get(`${process.env.REACT_APP_API_URL}/products`, { withCredentials: true })
      .then(res => {
        const names = res.data.map(p => p.name);
        const models = [...new Set(res.data.map(p => p.model).filter(Boolean))];
        const locations = [...new Set(res.data.map(p => p.location).filter(Boolean))];
        setAllProductNames(names);
        setAllModels(models);
        setAllLocations(locations);
      })
      .catch(() => console.error("Nie udało się pobrać danych produktów"));
  }, []);

  useEffect(() => {
    if (!form.name.trim()) return setSuggestedName("");
    const fuse = new Fuse(allProductNames, { includeScore: true });
    const result = fuse.search(form.name);
    if (result.length && result[0].score < 0.4) setSuggestedName(result[0].item);
    else setSuggestedName("");
  }, [form.name, allProductNames]);

  useEffect(() => {
    if (!form.model.trim()) return setSuggestedModel("");
    const fuse = new Fuse(allModels, { includeScore: true });
    const result = fuse.search(form.model);
    if (result.length && result[0].score < 0.4) setSuggestedModel(result[0].item);
    else setSuggestedModel("");
  }, [form.model, allModels]);

  useEffect(() => {
    if (!form.location.trim()) return setSuggestedLocation("");
    const fuse = new Fuse(allLocations, { includeScore: true });
    const result = fuse.search(form.location);
    if (result.length && result[0].score < 0.4) setSuggestedLocation(result[0].item);
    else setSuggestedLocation("");
  }, [form.location, allLocations]);

  const handleChange = (e) => {
    const { name, value, files, options } = e.target;
    if (name === "images") {
      setForm((prev) => ({ ...prev, images: files }));
      return;
    }
    if (name === "auction_platform") {
      const selected = Array.from(options).filter(opt => opt.selected).map(opt => opt.value);
      const cleanedForm = {
        ...form,
        auction_platform: selected,
      };
      if (!selected.includes("allegro")) cleanedForm.auction_allegro_url = "";
      if (!selected.includes("olx")) cleanedForm.auction_olx_url = "";
      setForm(cleanedForm);
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    for (const file of form.images) {
      if (file.size > MAX_FILE_SIZE) {
        setErrorMessage(`❌ Plik "${file.name}" jest większy niż 5MB. Zmniejsz rozmiar przed dodaniem.`);
        setSuccessMessage("");
        return;
      }
    }
    const data = new FormData();
    Object.entries(form).forEach(([key, val]) => {
      if (key === "images") {
        for (let i = 0; i < val.length; i++) {
          data.append("images[]", val[i]);
        }
      } else if (key === "auction_platform") {
        const joined = Array.isArray(val) && val.length > 0 ? val.join(",") : "";
        data.append(key, joined);
      } else if (key === "is_flagged") {
        data.append("is_flagged", form.is_flagged ? 1 : 0);
      } else {
        data.append(key, val);
      }
    });
    api
      .post(`${process.env.REACT_APP_API_URL}/products`, data, { withCredentials: true })
      .then(() => {
        setForm(initialState);
        setSuccessMessage("✅ Produkt został dodany pomyślnie.");
        setErrorMessage("");
        setTimeout(() => setSuccessMessage(""), 5000);
      })
      .catch((err) => {
        console.error(err.response?.data);
        setErrorMessage("❌ Wystąpił błąd podczas dodawania produktu.");
        setSuccessMessage("");
        setTimeout(() => setErrorMessage(""), 5000);
      });
  };

  return (
    <div className="container-xl py-4">
      <h2 className="mb-4">➕ Dodaj produkt</h2>
      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
      <form onSubmit={handleSubmit}>
        <div className="row g-3">
          <div className="col-12 col-md-4">
            <label className="form-label"><i className="fas fa-tag me-2 text-secondary" /> Nazwa</label>
            <input name="name" className="form-control" value={form.name} onChange={handleChange} required />
            {suggestedName && (
              <div className="form-text text-warning">
                Czy chodziło Ci o: <strong>{suggestedName}</strong>? <button type="button" className="btn btn-link p-0" onClick={() => { setForm((prev) => ({ ...prev, name: suggestedName })); setSuggestedName(""); }}>Użyj</button>
              </div>
            )}
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label"><i className="fas fa-hashtag me-2 text-secondary" /> Nr katalogowy</label>
            <input name="catalog_number" className="form-control" value={form.catalog_number} onChange={handleChange} />
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label"><i className="fas fa-cogs me-2 text-secondary" /> Model</label>
            <input name="model" className="form-control" value={form.model} onChange={handleChange} required />
            {suggestedModel && (
              <div className="form-text text-warning">
                Czy chodziło Ci o: <strong>{suggestedModel}</strong>? <button type="button" className="btn btn-link p-0" onClick={() => { setForm((prev) => ({ ...prev, model: suggestedModel })); setSuggestedModel(""); }}>Użyj</button>
              </div>
            )}
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label"><i className="fas fa-warehouse me-2 text-secondary" /> St.M</label>
            <input name="location" className="form-control" value={form.location} onChange={handleChange} />
            {suggestedLocation && (
              <div className="form-text text-warning">
                Czy chodziło Ci o: <strong>{suggestedLocation}</strong>? <button type="button" className="btn btn-link p-0" onClick={() => { setForm((prev) => ({ ...prev, location: suggestedLocation })); setSuggestedLocation(""); }}>Użyj</button>
              </div>
            )}
          </div>
          <div className="col-6 col-md-4">
            <label className="form-label"><i className="fas fa-sort-numeric-up me-2 text-secondary" /> Ilość</label>
            <input type="number" name="quantity" className="form-control no-spin" value={form.quantity} onChange={handleChange} min={0} onWheel={(e) => e.target.blur()} />
          </div>
          <div className="col-6 col-md-4">
            <label className="form-label"><i className="fas fa-ruler me-2 text-secondary" /> Jednostka</label>
            <select name="unit" className="form-select" value={form.unit} onChange={handleChange}>
              <option value="">-- wybierz --</option>
              <option value="szt">szt</option>
              <option value="kpl">kpl</option>
            </select>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label"><i className="fas fa-money-bill-wave me-2 text-secondary" /> Cena</label>
            <input type="number" name="price" className="form-control no-spin" value={form.price} onChange={handleChange} onWheel={(e) => e.target.blur()}  />
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label"><i className="fas fa-balance-scale me-2 text-secondary" /> Typ ceny</label>
            <select name="price_type" className="form-select" value={form.price_type} onChange={handleChange}>
              <option value="up">Cena w górę</option>
              <option value="down">Cena w dół</option>
              <option value="constant">Cena stała</option>
            </select>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label d-block">
              <i className="fas fa-flag me-2 text-danger" /> Oflaguj
            </label>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="is_flagged"
                name="is_flagged"
                checked={!!form.is_flagged}
                onChange={(e) => setForm(prev => ({ ...prev, is_flagged: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="is_flagged">
                Wyróżnij na liście
              </label>
            </div>
          </div>
          <div className="col-12 col-md-4">
            <label className="form-label"><i className="fas fa-store me-2 text-secondary" /> Aukcje</label>
            <select name="auction_platform" className="form-select" value={form.auction_platform} multiple onChange={handleChange}>
              <option value="allegro">Allegro</option>
              <option value="olx">OLX</option>
            </select>
          </div>
          {form.auction_platform.includes("allegro") && (
            <div className="col-12 col-md-4">
              <label className="form-label"><i className="fab fa-algolia me-2 text-warning" /> Link do Allegro</label>
              <input name="auction_allegro_url" className="form-control" value={form.auction_allegro_url || ""} onChange={handleChange} />
            </div>
          )}
          {form.auction_platform.includes("olx") && (
            <div className="col-12 col-md-4">
              <label className="form-label"><i className="fas fa-store me-2 text-primary" /> Link do OLX</label>
              <input name="auction_olx_url" className="form-control" value={form.auction_olx_url || ""} onChange={handleChange} />
            </div>
          )}
          <div className="col-12 col-md-8">
            <label className="form-label"><i className="fas fa-align-left me-2 text-secondary" /> Opis</label>
            <textarea name="description" className="form-control" style={{ overflow: "hidden", resize: "none", minHeight: "100px" }} value={form.description} onChange={handleChange} onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = `${e.target.scrollHeight}px`;
            }} />
          </div>
          <div className="col-12">
            <label className="form-label"><i className="fas fa-image me-2 text-secondary" /> Zdjęcia (jpg, max 5MB każde)</label>
            <input type="file" name="images" className="form-control" multiple accept=".jpg" onChange={handleChange} />
          </div>
          <div className="col-12 d-flex justify-content-end">
            <button type="submit" className="btn btn-success">
              💾 Dodaj produkt
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;